import React from "react";
import { Flex, Box, Text } from "@chakra-ui/react";
import { sectionSpacingY } from "components/section";
import { useColorThemeToggle } from "hooks/use-color-theme-toggle";
import { horizontalContainerPadding } from "ui/container";
import { type ThemeBgColor } from "contexts/color-theme";
import { Diptych } from "ui/diptych/diptych";
import { TextTitle } from "ui/text";
import { AnimationVideo } from "ui/animation-video";

const diptychConfig = [
  {
    title: "Complete context",
    description: (
      <>
        <Text>Hightouch Agents are powered by the same data that drives your business.</Text>
        <br />
        <Text>
          They connect directly to your data warehouse and marketing channels, ensuring every analysis and
          recommendation reflects real, up-to-date information.
        </Text>
      </>
    ),
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/ea1e3505c3f5a289b16d921166c4c30fe871ba09.mp4",
  },
  {
    title: "Expert-level marketing skill",
    description: (
      <>
        <Text>Hightouch AI Agents don&apos;t just understand data, they understand marketing.</Text>
        <br />
        <Text>
          Preloaded with deep domain knowledge, they can reason about complex concepts like creative fatigue,
          attribution modeling, and incrementality with expert precision.
        </Text>
      </>
    ),
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/9899f68e5cb08ac046658aeac93127918befddfe.mp4",
  },
  {
    title: "Take action with your agents",
    description: (
      <>
        <Text>Hightouch AI Agents don&apos;t stop at recommendations—they act on them.</Text>
        <br />
        <Text>
          Connected to all your marketing and business systems, they can build, launch, manage, and optimize campaigns
          across hundreds of marketing channels in real-time.
        </Text>
      </>
    ),
    animationMp4: "https://cdn.sanity.io/files/pwmfmi47/production/df34ebbcbc8cc97475b366c5da961adffa33c69d.mp4",
  },
];

export function AgentsHowItWorks() {
  const scope = useColorThemeToggle({ theme: "light", bgColor: "#EDFCFE" as ThemeBgColor });

  return (
    <Box
      ref={scope}
      id="how-it-works"
      as="section"
      w="100%"
      pos="relative"
      overflow="hidden"
      bg="#EDFCFE"
      pt={{ base: "huge", md: "hugest", lg: "224px" }}
      pb={{ base: "biggest", lg: "hugest" }}
      px={horizontalContainerPadding}
      mt={{ base: "-32px", md: "-64px" }}
      zIndex={-1}
    >
      <Box pos="relative" w="100%" maxW="container.section" mx="auto">
        <TextTitle
          align="center"
          size="md"
          title={
            <Text
              as="h2"
              fontSize={{ base: "24px", md: "40px" }}
              fontWeight="bold"
              lineHeight={{ base: 1.3, md: 1.2 }}
              whiteSpace="pre-wrap"
            >
              Agents purpose-built with marketing skill and context
            </Text>
          }
          titleAs="div"
          description={
            <Text fontSize={{ base: "16px", md: "18px" }} lineHeight={1.5} color="brand.textSecondary.light">
              Hightouch Agents are pre-trained for marketing and connect to all your data, channels, and creative.
              Incredibly useful, without endless prompting.
            </Text>
          }
          descriptionAs="div"
        />

        <Flex flexDir="column" gap={sectionSpacingY} mt={{ base: "biggest", md: "huger" }}>
          {diptychConfig.map((item, index) => {
            const direction = index % 2 === 0 ? "right" : "left";
            const mediaRatio = 626 / 430;

            return (
              <Diptych
                key={item.title}
                direction={direction}
                size="md"
                theme="light"
                title={
                  <Text as="h3" fontSize={{ base: "20px", md: "24px" }} fontWeight="bold" lineHeight={1.2}>
                    {item.title}
                  </Text>
                }
                titleAs="div"
                description={
                  <Text
                    as="div"
                    fontSize={{ base: "14px", md: "16px" }}
                    lineHeight={1.4}
                    color="brand.textSecondary.light"
                  >
                    {item.description}
                  </Text>
                }
                descriptionAs="div"
                mediaRatio={mediaRatio}
                media={<AnimationVideo hideControls isLooped mp4={item.animationMp4} poster="" viewportAmount={0.7} />}
              />
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
}
